import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import {
    FONT_SIZE_XS,
    BUTTON_FONT_FAMILY,
    BUTTON_FONT_WEIGHT,
    BUTTON_LETTER_SPACING,
} from "../../../style/fontSettings";
import {
    MELOGY_WHITE,
    MELOGY_TRANSPARENT,
    SECONDARY,
} from "../../../style/colors";
import { showToastError } from "../../../style/ShowToast";

const StyleNameContainer = styled.div`
    height: 64px;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: ${MELOGY_TRANSPARENT};
`;

const StyleNameBackground = styled.div`
    color: ${SECONDARY};
    background-color: ${MELOGY_TRANSPARENT};
    width: 80%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
`;

const StyleNameInput = styled.input`
    font-family: ${BUTTON_FONT_FAMILY};
    font-weight: ${BUTTON_FONT_WEIGHT};
    letter-spacing: ${BUTTON_LETTER_SPACING};
    font-size: ${FONT_SIZE_XS};
    height: 32px;
    width: 100%;
    color: ${MELOGY_WHITE};
    background-color: ${MELOGY_TRANSPARENT};
    border: none;
    text-align: center;

    &:focus {
        outline: none;
    }
`;

const StyleName = ({
    styles, 
    color, 
    selectedStyle, 
    updateStyle,
}) => {
    const getStyleName = useCallback((style) => {
        if (!Array.isArray(style)) {
            return "";
        }
        return style.find((item) => item.name === "styleName")?.value || "";
    }, []);

    const originalStyleName = getStyleName(selectedStyle);
    const [tempName, setTempName] = useState(originalStyleName);

    useEffect(() => {
        setTempName(getStyleName(selectedStyle));
    }, [selectedStyle, getStyleName]);

    const isNameUnique = useCallback(
        (name) => {
            if (!Array.isArray(styles)) {
                return true; // If styles is not an array, assume name is unique
            }
            return !styles.some((style) => {
                const styleName = getStyleName(style);
                return styleName === name && style !== selectedStyle;
            });
        },
        [styles, getStyleName, selectedStyle]
    );

    const validateAndSetStyleName = () => {
        // Skip validation if the name hasn't changed
        if (tempName === originalStyleName) {
            return;
        }

        // Validate characters
        const regex = /^[a-zA-Z0-9.\-_, !?]+$/;
        if (!regex.test(tempName)) {
            showToastError("Invalid characters in the style name", 5000);
            setTempName(originalStyleName);
            return;
        }

        // Validate length
        if (tempName.length > 42) {
            showToastError("Style name should be at most 42 characters", 5000);
            setTempName(originalStyleName);
            return;
        }

        // Validate uniqueness
        if (!isNameUnique(tempName)) {
            showToastError("Style name should be unique", 5000);
            setTempName(originalStyleName);
            return;
        }

        // Update the style if all validations pass
        updateStyle("styleName", tempName);
    };

    return (
        <StyleNameContainer>
            <StyleNameBackground>
                <StyleNameInput
                    value={tempName}
                    onChange={(e) => setTempName(e.target.value)}
                    onBlur={validateAndSetStyleName}
                    style={{ color }} 
                    spellCheck={false}
                />
            </StyleNameBackground>
        </StyleNameContainer>
    );
};

export default StyleName;